import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@dukene/shared';
import {
    Truck,
    Package,
    MapPin,
    Clock,
    CheckCircle2,
    AlertCircle,
    ScanLine,
    ArrowRight,
    Navigation,
    Search,
    RefreshCw,
    Filter,
    ChevronDown,
    ChevronRight,
    Store,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ScanEvent {
    id: string;
    eventType: string;
    occurredAt: string;
    notes?: string;
    location?: { id: string; name: string; address?: string };
    scannedBy?: { id: string; name: string; role: string };
}

interface ShipmentRow {
    id: string;
    trackingCode: string;
    status: string;
    tier: string;
    shippingCost: number;
    weightKg: number;
    estimatedDelivery?: string;
    collectedAt?: string;
    createdAt: string;
    originLocation: { id: string; name: string; address?: string };
    destinationLocation: { id: string; name: string; address?: string };
    currentLocation?: { id: string; name: string };
    assignedAgent?: { id: string; name: string; role: string };
    events: ScanEvent[];
    order: {
        id: string;
        orderNumber: string;
        status: string;
        email: string;
        total: number;
        createdAt: string;
        store?: { name: string; subdomain: string };
        customer?: { firstName: string; lastName: string; email: string };
    };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any; dot: string }> = {
    PENDING: { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock, dot: 'bg-amber-400' },
    CHECKED_IN: { label: 'Checked In', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: ScanLine, dot: 'bg-blue-500' },
    IN_TRANSIT: { label: 'In Transit', color: 'bg-violet-50 text-violet-700 border-violet-200', icon: Truck, dot: 'bg-violet-500' },
    AT_DESTINATION: { label: 'At Destination', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: MapPin, dot: 'bg-emerald-500' },
    COLLECTED: { label: 'Collected', color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2, dot: 'bg-green-500' },
    FAILED: { label: 'Failed', color: 'bg-red-50 text-red-700 border-red-200', icon: AlertCircle, dot: 'bg-red-500' },
};

const EVENT_CONFIG: Record<string, { label: string; icon: any; color: string }> = {
    CHECKED_IN: { label: 'Checked In', icon: ScanLine, color: 'text-blue-600 bg-blue-50 border-blue-200' },
    DEPARTED: { label: 'Departed', icon: ArrowRight, color: 'text-violet-600 bg-violet-50 border-violet-200' },
    ARRIVED: { label: 'Arrived', icon: Navigation, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
    OUT_FOR_DELIVERY: { label: 'Out for Delivery', icon: Truck, color: 'text-orange-600 bg-orange-50 border-orange-200' },
    COLLECTED: { label: 'Collected', icon: CheckCircle2, color: 'text-green-600 bg-green-50 border-green-200' },
    EXCEPTION: { label: 'Exception', icon: AlertCircle, color: 'text-red-600 bg-red-50 border-red-200' },
};

const STATUSES = ['PENDING', 'CHECKED_IN', 'IN_TRANSIT', 'AT_DESTINATION', 'COLLECTED', 'FAILED'];

const PROGRESS_MAP: Record<string, number> = {
    PENDING: 5, CHECKED_IN: 25, IN_TRANSIT: 55, AT_DESTINATION: 80, COLLECTED: 100, FAILED: 100,
};

function formatTime(iso: string) {
    return new Date(iso).toLocaleString('en-KE', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-KE', {
        year: 'numeric', month: 'short', day: 'numeric',
    });
}

// ─── Scan Timeline ────────────────────────────────────────────────────────────

function ScanTimeline({ events }: { events: ScanEvent[] }) {
    if (events.length === 0) {
        return (
            <div className="flex items-center gap-2 py-3 px-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                <Clock className="w-4 h-4 text-gray-400" />
                <p className="text-xs text-gray-400 italic">No scans yet — waiting for package drop-off at origin node</p>
            </div>
        );
    }

    return (
        <ol className="relative border-l-2 border-gray-200 ml-3 space-y-0">
            {events.map((ev, idx) => {
                const cfg = EVENT_CONFIG[ev.eventType] ?? { label: ev.eventType, icon: ScanLine, color: 'text-gray-600 bg-gray-50 border-gray-200' };
                const Icon = cfg.icon;
                return (
                    <li key={ev.id} className={`ml-4 ${idx !== events.length - 1 ? 'pb-4' : 'pb-0'}`}>
                        <span className={`absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full border ${cfg.color}`}>
                            <Icon className="w-2.5 h-2.5" />
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${cfg.color}`}>
                                {cfg.label}
                            </span>
                            <span className="text-[11px] text-gray-400">{formatTime(ev.occurredAt)}</span>
                        </div>
                        {ev.location && (
                            <p className="text-xs text-gray-700 mt-0.5 font-medium">
                                <MapPin className="inline w-3 h-3 mr-1 text-gray-400" />
                                {ev.location.name}
                                {ev.location.address && (
                                    <span className="text-gray-400 font-normal"> · {ev.location.address}</span>
                                )}
                            </p>
                        )}
                        {ev.scannedBy && (
                            <p className="text-[11px] text-gray-400 mt-0.5">
                                Scanned by{' '}
                                <span className="font-semibold text-gray-600">{ev.scannedBy.name}</span>
                                <span className="ml-1 px-1 bg-gray-100 rounded text-[10px] uppercase tracking-wide">
                                    {ev.scannedBy.role}
                                </span>
                            </p>
                        )}
                        {ev.notes && (
                            <p className="text-[11px] text-gray-400 italic mt-0.5">{ev.notes}</p>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}

// ─── Shipment Card ────────────────────────────────────────────────────────────

function ShipmentCard({ shipment }: { shipment: ShipmentRow }) {
    const [expanded, setExpanded] = useState(false);
    const cfg = STATUS_CONFIG[shipment.status] ?? STATUS_CONFIG.PENDING;
    const StatusIcon = cfg.icon;
    const progress = PROGRESS_MAP[shipment.status] ?? 0;

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all">
            {/* Card header */}
            <div className="p-4 flex items-start gap-4">
                {/* Status icon */}
                <div className={`mt-0.5 flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border ${cfg.color}`}>
                    <StatusIcon className="w-4 h-4" />
                </div>

                <div className="flex-1 min-w-0">
                    {/* Tracking + badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-mono font-bold text-gray-900 text-sm">{shipment.trackingCode}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cfg.color}`}>
                            {cfg.label}
                        </span>
                        <span className="text-[10px] font-medium uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                            {shipment.tier?.replace('_', ' ')}
                        </span>
                    </div>

                    {/* Order + store info */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            Order{' '}
                            <strong className="text-gray-800">#{shipment.order.orderNumber}</strong>
                        </span>
                        {shipment.order.store && (
                            <span className="flex items-center gap-1">
                                <Store className="w-3 h-3" />
                                <strong className="text-gray-700">{shipment.order.store.name}</strong>
                            </span>
                        )}
                        {shipment.order.customer && (
                            <span>
                                {shipment.order.customer.firstName} {shipment.order.customer.lastName}
                            </span>
                        )}
                        <span>{formatDate(shipment.createdAt)}</span>
                        <span>KES {Number(shipment.shippingCost).toFixed(0)}</span>
                        <span>{Number(shipment.weightKg).toFixed(1)} kg</span>
                    </div>

                    {/* Route arrows */}
                    <div className="flex items-center gap-1.5 flex-wrap text-xs mb-3">
                        <span className="flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded-md text-gray-700 font-medium">
                            <MapPin className="w-3 h-3 text-emerald-500" />
                            {shipment.originLocation.name}
                        </span>
                        <ArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                        {shipment.currentLocation &&
                            shipment.currentLocation.id !== shipment.destinationLocation.id && (
                                <>
                                    <span className="flex items-center gap-1 px-2 py-1 bg-violet-50 border border-violet-200 rounded-md text-violet-700 font-medium">
                                        <Navigation className="w-3 h-3" />
                                        {shipment.currentLocation.name}
                                    </span>
                                    <ArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                                </>
                            )}
                        <span className="flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded-md text-gray-700 font-medium">
                            <MapPin className="w-3 h-3 text-red-500" />
                            {shipment.destinationLocation.name}
                        </span>
                    </div>

                    {/* Progress bar */}
                    {shipment.status !== 'FAILED' && (
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-700 ${shipment.status === 'COLLECTED' ? 'bg-green-500' : 'bg-violet-500'
                                    }`}
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    )}
                </div>

                {/* Expand toggle */}
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="flex-shrink-0 flex flex-col items-end gap-1.5 ml-1"
                >
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                        <ScanLine className="w-3.5 h-3.5" />
                        {shipment.events.length} scan{shipment.events.length !== 1 ? 's' : ''}
                    </span>
                    {expanded
                        ? <ChevronDown className="w-4 h-4 text-gray-400" />
                        : <ChevronRight className="w-4 h-4 text-gray-400" />
                    }
                </button>
            </div>

            {/* Expanded scan timeline */}
            {expanded && (
                <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-1.5">
                        <ScanLine className="w-3.5 h-3.5" />
                        Scan Timeline
                    </p>
                    <ScanTimeline events={shipment.events} />

                    <div className="mt-3 space-y-1">
                        {shipment.estimatedDelivery && (
                            <p className="text-xs text-gray-500">
                                Est. delivery:{' '}
                                <strong className="text-gray-700">{formatDate(shipment.estimatedDelivery)}</strong>
                            </p>
                        )}
                        {shipment.collectedAt && (
                            <p className="text-xs text-green-700 font-semibold">
                                ✓ Collected {formatTime(shipment.collectedAt)}
                            </p>
                        )}
                        {shipment.assignedAgent && (
                            <p className="text-xs text-gray-500">
                                Shipping agent:{' '}
                                <strong className="text-gray-700">{shipment.assignedAgent.name}</strong>
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, colorClass }: {
    label: string; value: number | string; icon: any; colorClass: string;
}) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <p className="text-xl font-black text-gray-900 leading-none">{value}</p>
                <p className="text-xs text-gray-500 font-medium mt-0.5">{label}</p>
            </div>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OrderRoutes() {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['admin-order-routes', page, debouncedSearch, statusFilter],
        queryFn: () => {
            const params = new URLSearchParams({
                page: String(page),
                limit: '15',
                ...(debouncedSearch && { search: debouncedSearch }),
                ...(statusFilter && { status: statusFilter }),
            });
            return apiClient.get<{
                shipments: ShipmentRow[];
                pagination: { total: number; page: number; pages: number };
            }>(`/shipping/shipments?${params}`);
        },
        refetchInterval: 30000,
    });

    const shipments = data?.shipments ?? [];
    const pagination = data?.pagination ?? { total: 0, page: 1, pages: 1 };

    const counts = {
        pending: shipments.filter(s => s.status === 'PENDING').length,
        inTransit: shipments.filter(s => s.status === 'IN_TRANSIT').length,
        collected: shipments.filter(s => s.status === 'COLLECTED').length,
        failed: shipments.filter(s => s.status === 'FAILED').length,
    };

    return (
        <div className="space-y-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-4 border-b border-gray-100">
                <div>
                    <h1 className="text-2xl font-black tracking-tight text-gray-900 flex items-center gap-2">
                        <Truck className="w-6 h-6 text-violet-600" />
                        Order Routes
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Full scan-by-scan timeline of every shipment across the Dukene network.
                    </p>
                </div>
                <button
                    onClick={() => refetch()}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 shadow-sm transition-all"
                >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Total" value={pagination.total} icon={Package} colorClass="bg-gray-100 text-gray-600" />
                <StatCard label="Pending" value={counts.pending} icon={Clock} colorClass="bg-amber-50 text-amber-600" />
                <StatCard label="In Transit" value={counts.inTransit} icon={Truck} colorClass="bg-violet-50 text-violet-600" />
                <StatCard label="Collected" value={counts.collected} icon={CheckCircle2} colorClass="bg-green-50 text-green-600" />
                <StatCard label="Failed" value={counts.failed} icon={AlertCircle} colorClass="bg-red-50 text-red-600" />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm flex-1 max-w-sm">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                        type="text"
                        placeholder="Search tracking code..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                        className="bg-transparent border-none outline-none text-sm flex-1 text-gray-700 placeholder-gray-400"
                    />
                </div>

                <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                        value={statusFilter}
                        onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                        className="bg-transparent border-none outline-none text-sm text-gray-700"
                    >
                        <option value="">All Statuses</option>
                        {STATUSES.map(s => (
                            <option key={s} value={s}>{STATUS_CONFIG[s]?.label ?? s}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Shipment cards */}
            {isLoading ? (
                <div className="space-y-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
                    ))}
                </div>
            ) : shipments.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-24 bg-white border border-dashed border-gray-200 rounded-xl">
                    <Truck className="w-12 h-12 text-gray-200" />
                    <p className="text-gray-400 font-medium text-sm">No shipments found</p>
                    <p className="text-gray-300 text-xs text-center max-w-xs">
                        Shipments appear here once merchant orders are paid and routes are created automatically.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {shipments.map(s => (
                        <ShipmentCard key={s.id} shipment={s} />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="flex items-center justify-between pt-2">
                    <p className="text-sm text-gray-500">
                        Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                            className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors"
                        >
                            Previous
                        </button>
                        <button
                            disabled={page >= pagination.pages}
                            onClick={() => setPage(p => p + 1)}
                            className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
